import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { CourseAliases } from 'redux/schemas/models/course';
import { getCourseAliases, getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { halfSpacing, quarterSpacing, standardSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import prodPathReplace from 'shared/prod-path-rewrite';
import { black, gray4, hexToRgbaString, primary, white } from 'styles/global_defaults/colors';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { getCurrentUser } from 'redux/selectors/users';
import { GEN_AI_ARTICLE_LINK } from 'institutions/components/advanced-settings/ai-hub/generative-ai-capabilities';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { RootState } from 'redux/schemas';
import ClickableContainer from 'components/clickable-container';
import AICommonStyles from 'lecture_pages/components/left-panel/novo-ai/components/shared/ai-common-styles';
import { isEmpty } from 'underscore';
import { screenMdMin } from 'styles/global_defaults/media-queries';
import { Institution } from 'redux/schemas/models/institution';
import NvPopover from 'shared/components/nv-popover';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle } from 'shared/components/inputs/nv-dropdown';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { selectOutlineFile } from 'redux/actions/ai-course-creation';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { wrapThunkAction } from 'redux/utils';
import { config } from '@config/config.json';


export const AIButton = ({ onClick, text, dataQa, disabled = false }) => {
  const styles = css`
    ${AICommonStyles};
    .novo-ai-btn  {
      position: relative;
      border-radius: 50px;
      border: ${disabled ? `2px solid ${gray4}` : '2px solid transparent'};
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
      box-shadow: none ;
      gap:${halfSpacing}px;
      background-color: transparent; /* Transparent by default */
      transition: background-color 0.3s; /* Smooth transition for hover effect */
      z-index: 0; /* Ensure the .gradient-btn is on top */

      ${!disabled && `
        ::before {
          content: "";
          position: absolute;
          top: 0px;
          right: 0px;
          bottom: 0px;
          left: 0px;
          border-radius: inherit; /* Ensure the border radius is applied */
          padding: 2px; /* Adjust to match the border width */
          background: linear-gradient(48.41deg, #60F3AB 0%, #00CCBC 45%, #009AC0 100%);
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: destination-out;
          mask-composite: exclude; /* Make the center part transparent */
          z-index: -1;
        }

        &:active {
          border-color: transparent !important;
          box-shadow: none !important;
          background-color: transparent !important;
        }
        &:hover {
          background-color: transparent !important;
        }
      `}
    }

    .novo-ai-text {
      color: ${disabled ? gray4 : black};
    }
  `;
  return (
    <div css={styles}>
      <Button
        className='novo-ai-btn d-flex align-items-center py-2 px-5 m-auto'
        onClick={onClick}
        data-qa={dataQa}
        disabled={disabled}
      >
        <NvIcon icon='gen-ai' size='small' className={disabled ? 'text-gray-4' : 'text-ai-color'} />
        <span className='novo-ai-text bold text-large-regular'>{text}</span>
      </Button>
    </div>
  );
};

export const SetVisualStylingContent = () => {
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));
  return (
    <div>
      <div className='course-listing-txt1 page-title text-align-center'>{t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LISTING.COURSE_LISTING_TEXT_ONE()}</div>
      <div className='course-listing-txt2 text-body text-align-left'>
        {t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LISTING.COURSE_LISTING_TEXT_TWO(aliases.courseAliases)}
        <a
          href={GEN_AI_ARTICLE_LINK}
          target='_blank'
          rel='noreferrer'
          className='learn-more text-primary'
          data-qa={config.pendo.novoAiCourseCreation.courseListing.learnMore}
        >
          {t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LISTING.LEARN_MORE()}
        </a>
      </div>
    </div>
  );
};

interface CourseLengthOptionsProps {
  handleGenerate: (selectedLength: string) => void;
  isGenerateDisabled?: boolean;
  generateButtonTooltipText: string;
}

export const CourseLengthOptions = ({ handleGenerate, isGenerateDisabled = false, generateButtonTooltipText }: CourseLengthOptionsProps) => {
  const preferencesStyles = css`
    ${AICommonStyles};
    width: 280px;
    margin: auto;

    .title {
      padding: ${standardSpacing}px ${standardSpacing}px ${halfSpacing}px ${standardSpacing}px;
    }
    .options {
      .option {
        width: 94px;
        height: 70px;
        .icon {
          padding: ${halfSpacing}px;
        }
      }
    }
  `;

  const [selectedLength, setSelectedLength] = useState('medium');
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));
  const { selectedCourseId } = useSelector((state) => state.app.aiCourseCreation);


  const lengthOptions = [
    {
      key: 'short',
      icon: 'length',
      label: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH_TYPE.SHORT(),
      tooltip: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH.SHORT(),
    },
    {
      key: 'medium',
      icon: 'length-medium',
      label: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH_TYPE.MEDIUM(),
      tooltip: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH.MEDIUM(),
    },
    {
      key: 'long',
      icon: 'length-long',
      label: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH_TYPE.LONG(),
      tooltip: t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH.LONG(),
    },
  ];

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEmpty(selectedCourseId) && listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedCourseId]);


  return (
    <div>
      <div className='d-flex flex-column my-8' css={preferencesStyles}>
        <div className='page-title text-center mb-4' ref={listRef}>
          {t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_LENGTH_TITLE(aliases.courseAliases)}
        </div>
        <div className='length'>
          <div className='options d-flex align-items-center text-align-center justify-content-between'>
            {lengthOptions.map(({ key, icon, label, tooltip }) => (
              <NvTooltip
                key={key}
                text={tooltip}
                placement='bottom'
              >
                <ClickableContainer
                  onClick={() => setSelectedLength(key)}
                  data-qa={config.pendo.courseLengthOptions[key]}
                  aria-label={label}
                >
                  <div className={`option ${selectedLength === key ? 'selected' : ''}`}>
                    <NvIcon
                      icon={icon}
                      size='small'
                      className={selectedLength === key ? 'text-ai-color' : ''}
                    />
                    <span className='font-weight-bolder'>
                      {label}
                    </span>
                  </div>
                </ClickableContainer>
              </NvTooltip>
            ))}
          </div>
        </div>
      </div>
      <div>
        <NvTooltip
          placement='top'
          preventOverflow={false}
          enabled={isGenerateDisabled}
          text={generateButtonTooltipText}
        >
          <div>
            <AIButton
              text={t.LECTURE_PAGES.LHS.NOVO_AI.GENERATE_NOW()}
              onClick={() => handleGenerate(selectedLength)}
              dataQa={config.pendo.novoAiCourseCreation.generateNow}
              disabled={isGenerateDisabled}
            />
          </div>
        </NvTooltip>
        <div className='mt-2 text-align-center text-gray-2 text-small font-weight-bold'>{t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_REVIEW_ACCURACY()}</div>
      </div>
    </div>
  );
};

export const CourseItemCard = ({ course, handleCourseItem, handlePreviewCouse, selectedId = 0 }) => {
  const courseImg = course.coverPhotoUrl ? 'assets/brand/temp.png' : course.coverPhotoUrl;
  const { selectedCourseId } = useSelector((state) => state.app.aiCourseCreation);
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));
  const currentInstitution = useSelector<RootState, Institution>(getCurrentInstitution);

  const styles = css`
    .course-item {
      padding: ${halfSpacing}px ${standardSpacing}px;
      border-radius: ${quarterSpacing}px;

      &:focus-within,
      &:hover {
        background-color: ${hexToRgbaString('#00CCBC', 0.05)} !important;
        .view-icon {
          opacity: 1;
        }
      }
    }

    .course-img {
      width: ${tripleSpacing}px;
      height: ${tripleSpacing}px;
    }

    .course-item-text {
      width: 90%;
    }

    .view-icon {
      color: ${primary};
      opacity: 0;
      pointer-events: none;
    }

    .course-item.selected {
      padding: ${halfSpacing}px ${standardSpacing}px;
      position: relative;
      border-radius: ${quarterSpacing}px;
      background: #00CCBC0D;
      border: 2px solid transparent;
      cursor: pointer;
      margin-bottom: 0px !important;
    }

    .course-item.selected  {
      &::before{
        content: "";
        position: absolute;
        top: -3px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border-radius: ${quarterSpacing}px;
        padding: 2px;
        background: linear-gradient(45deg, #60F3AB, #00CCBC, #009AC0);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out;
        mask-composite: exclude;
        pointer-events: none;
      }
    }

    .text-ellipsis  {
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
    .course-title-container {
      @media (min-width: ${screenMdMin}px) {
        width: 180px !important;
      }
      @media (max-width: 500px) {
        width: 180px !important;
      }
    }
  `;

  const defaultImgStyle = css`
    width: ${tripleSpacing}px;
    height: ${tripleSpacing}px;
    margin-right: ${halfSpacing}px;
    background-color: ${currentInstitution.brandColor}
  `;

  return (
    <div css={styles}>
      <ClickableContainer
        className={`course-item d-flex align-items-center mb-4 ${selectedCourseId === course.id ? 'selected' : ''}`}
        onClick={handleCourseItem}
        data-qa={config.pendo.novoAiCourseCreation.courseItemCard}
        aria-label={course.name}
      >
        <div className='d-flex align-items-center course-item-text'>
          {course?.coverPhotoUrl !== '/assets/brand/temp.png' ? (
            <img className='course-img mr-2' src={course?.coverPhotoUrl} alt={t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_ITEMS.ALT_TEXT(aliases.courseAliases)} />
          ) : (<div css={defaultImgStyle} />)}
          <NvTooltip
            placement='bottom'
            text={course.name}
          >
            <div className='course-title-container'>
              <div className='course-title1 text-ellipsis text-regular bold'>{course.name}</div>
              <div className='course-title2 text-gray-2 label'>{course.catalogId}</div>
            </div>
          </NvTooltip>
        </div>
        <NvTooltip
          placement='top'
          text={t.LECTURE_PAGES.LHS.NOVO_AI.PREVIEW_COURSE_STYLE(aliases.courseAliases)}
        >
          <ClickableContainer
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewCouse(course);
            }}
            data-qa={config.pendo.novoAiCourseCreation.icons.previewCourseIcon}
            aria-label={t.LECTURE_PAGES.LHS.NOVO_AI.PREVIEW_COURSE_STYLE(aliases.courseAliases)}
          >
            <NvIcon className='view-icon p-1' icon='view' size='small' />
          </ClickableContainer>
        </NvTooltip>
      </ClickableContainer>
    </div>
  );
};

export const SearchWarningMessage = () => (
  <div>
    <div className='text-center color-danger'>{t.FORM.WARNING()}</div>
    <div className='mt-1'>
      {t.LHS.CONTENT_SEARCH.INVALID_SEARCH()}
    </div>
  </div>
);

export const WelcomeMessages = () => {
  const currentUser = useSelector(getCurrentUser);
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));

  return (
    <div className='text-align-center'>
      <div className='page-title'>{t.LECTURE_PAGES.LHS.NOVO_AI.WELCOME_TEXT.WELCOME_TEXT_ONE(currentUser.firstName)}</div>
      <div className='text-body mt-2 text-align-left'>{t.LECTURE_PAGES.LHS.NOVO_AI.WELCOME_TEXT.WELCOME_TEXT_TWO(aliases.courseAliases)}</div>
      <div className='my-2'> <LearnMoreInfo /> </div>
      <div className='page-title mb-2'>{t.LECTURE_PAGES.LHS.NOVO_AI.WELCOME_TEXT.WELCOME_TEXT_THREE()}</div>
      <div className='text-body'>{t.LECTURE_PAGES.LHS.NOVO_AI.WELCOME_TEXT.WELCOME_TEXT_FOUR(aliases.courseAliases)}</div>
    </div>
  );
};

export const LearnMoreInfo = () => {
  const popoverInfoStyles = css`
   min-width: 410px;

    .bs4-popover-body {
      padding: ${standardSpacing}px;
    }
  `;

  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));

  return (
    <React.Fragment>
      <NvPopover
        showOnHover
        placement='bottom'
        preventOverflow
        overlayStyles={popoverInfoStyles}
        content={(
          <React.Fragment>
            <div className='mb-2 text-regular font-weight-bolder text-black text-align-center'>{t.LECTURE_PAGES.LHS.NOVO_AI.AI_LEARN_MORE_INFO.TITLE(aliases.courseAliases)}</div>
            <div className='text-body'>{t.LECTURE_PAGES.LHS.NOVO_AI.AI_LEARN_MORE_INFO.DESCRIPTION({ ...aliases.courseAliases, ...aliases.assignmentAliases })}</div>
          </React.Fragment>
        )}
      >
        <div className='d-flex align-items-center'>
          <i className='icon icon-xs-smallest icon-info text-primary' aria-hidden='true' />
          <div className='ml-1 text-primary text-regular'>{t.LECTURE_PAGES.LHS.NOVO_AI.AI_LEARN_MORE_INFO.LEARN_MORE()}</div>
        </div>
      </NvPopover>
    </React.Fragment>
  );
};

export const SelectOutlineFile = ({ uploadedFilesList = [] }) => {
  const styles = css`
    .bs4-dropdown {
      background: none !important;

      .bs4-dropdown-menu {
        max-height: 180px !important;
        overflow-y: auto !important;
      }
    }
  `;
  const dispatch = useAppDispatch();
  const currentCourse = useSelector(getCurrentCourse);
  const params = useLecturePageParams();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const [selectedOutline, setSelectedOutline] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const completedUploadedFiles = uploadedFilesList.filter(file => file.status === 'success');

  const handleOutlineSelect = (fileId: number) => {
    setSelectedOutline(fileId);
    setIsDropdownOpen(false);

    wrapThunkAction(dispatch(
      selectOutlineFile({
        catalogId: currentCourse.catalogId,
        aiCreationSourceId: fileId,
      }),
    )).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    });
  };

  const fileDropdownItems = completedUploadedFiles.map((file) => {
    const isPageCountExceeded = typeof file.pageCount === 'number' && file.pageCount > 100;

    return {
      id: file.id,
      type: 'text' as const,
      text: file.name,
      textClassName: 'mw-100 text-wrap text-regular',
      callback: () => handleOutlineSelect(file.id),
      disabled: isPageCountExceeded,
      tooltip: {
        text: t.LECTURE_PAGES.LHS.NOVO_AI.SELECT_COURSE_OUTLINE.SELECT_OUTLINE_TOOLTIP_FOR_DISABLED_FILES(),
        enabled: isPageCountExceeded,
      },
    };
  });

  useEffect(() => {
    if (selectedOutline && !completedUploadedFiles.some(file => file.id === selectedOutline)) {
      setSelectedOutline(null);
    }
  }, [selectedOutline, completedUploadedFiles]);

  return (
    <div css={styles} className='mt-8'>
      <div className='page-title text-center'>{t.LECTURE_PAGES.LHS.NOVO_AI.SELECT_COURSE_OUTLINE.SELECT_OUTLINE_HEADER()}</div>
      <div className='text-body text-align-left my-2'>
        {t.LECTURE_PAGES.LHS.NOVO_AI.SELECT_COURSE_OUTLINE.SELECT_OUTLINE_TEXT()}
      </div>
      <div>
        <NvDropdown
          show={isDropdownOpen}
          onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
          useMaxHeightModifier
          maxWidth={320}
          minWidth={320}
          buttonStyle={NvDropdownButtonStyle.FORM}
          items={fileDropdownItems}
          title={
            selectedOutline
              ? uploadedFilesList.find(file => file.id === selectedOutline)?.name
              : t.LECTURE_PAGES.LHS.NOVO_AI.SELECT_COURSE_OUTLINE.SELECT_OUTLINE_PLACEHOLDER()
          }
          titleClass={selectedOutline ? '' : 'text-gray-2'}
          align={NvDropdownAlign.CENTER}
          flip={false}
          drop='down'
        />
      </div>
      {selectedOutline && (
        <div className='mt-2 text-regular'>
          {t.LECTURE_PAGES.LHS.NOVO_AI.SELECT_COURSE_OUTLINE.SELECT_OUTLINE_NOTE_TEXT(courseAliases)}
        </div>
      )}
    </div>
  );
};
